import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { getAzAccount } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { resolveRepoRoot } from "../../lib/brain-paths.js";
import { deployPromptAdvisor } from "../../lib/deploy-prompt-advisor.js";

export class AgentDeployAdvisorCommand extends M8tCommand {
  static paths = [["agent", "deploy-advisor"]];
  static usage = Command.Usage({
    description: "Deploy a brain-eligible prompt advisor headlessly from its persona frontmatter.",
    details:
      "Reads the persona frontmatter (model, reasoning effort, fillable-field defaults) from" +
      " personas/<persona>/persona.md, renders the instructions, and creates a new Foundry" +
      " prompt-agent version under <name>. Idempotent — re-running creates a new version." +
      " Used by the cloud installer to seed the default advisor worker set.",
    examples: [
      [
        "Deploy Stacey from the startup-advisor persona",
        "$0 agent deploy-advisor --persona startup-advisor --name stacey",
      ],
      [
        "Deploy with an explicit endpoint (non-interactive)",
        "$0 agent deploy-advisor --persona startup-advisor --name stacey --endpoint https://myaccount.services.ai.azure.com/api/projects/myproject",
      ],
      [
        "Override the model",
        "$0 agent deploy-advisor --persona startup-advisor --name stacey --model gpt-4.1-mini",
      ],
    ],
  });

  persona = Option.String("--persona", {
    description: "Persona directory name under personas/ (e.g. startup-advisor).",
  });
  name = Option.String("--name", {
    description: "The agent instance name to deploy as (e.g. stacey).",
  });
  model = Option.String("--model", {
    description: "Override the model from persona frontmatter.",
  });
  endpoint = Option.String("--endpoint", {
    description: "Foundry project endpoint URL. If omitted, resolved from the subscription.",
  });
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID. Defaults to the current az account.",
  });
  repoRootFlag = Option.String("--repo-root", {
    description: "Path to the m8t repo checkout. Defaults to the ~/.m8t/repo-root marker.",
  });
  output = Option.String("--output", {
    description: "Output format: pretty (default) or json.",
  });

  async executeCommand(): Promise<number> {
    // Normalize clipanion descriptor objects that appear when flags are unset in tests.
    const persona = typeof this.persona === "string" ? this.persona : undefined;
    const agentName = typeof this.name === "string" ? this.name : undefined;

    if (!persona) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--persona is required (e.g. --persona startup-advisor).",
        hint: "Pass the persona directory name under personas/ in your m8t checkout.",
      });
    }
    if (!agentName) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--name is required (e.g. --name stacey).",
        hint: "Pass the agent instance name to deploy as.",
      });
    }

    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? this.output
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const onProgress =
      mode === "pretty"
        ? (m: string) => {
            this.context.stderr.write(`  ${colors.dim(m)}\n`);
          }
        : undefined;

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;

    onProgress?.("resolving Foundry project…");
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
    });

    // Resolve the repo root: prefer --repo-root flag, then the ~/.m8t/repo-root marker.
    const repoRoot =
      (typeof this.repoRootFlag === "string" ? this.repoRootFlag : undefined) ??
      resolveRepoRoot();

    onProgress?.(`deploying advisor '${persona}' as '${agentName}'…`);
    const version = await deployPromptAdvisor({
      credential,
      endpoint: project.endpoint,
      repoRoot,
      persona,
      agentName,
      model: typeof this.model === "string" ? this.model : undefined,
      // undefined in --output json, which is how the installer runs this command:
      // its retry classifier jq-parses the captured stream and a stray line there
      // would make a transient failure look non-retryable.
      ...(onProgress ? { onNotice: onProgress } : {}),
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({ name: agentName, persona, version }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} deployed advisor ${colors.field(agentName)} ` +
        `(persona ${colors.field(persona)}, version ${version})\n`,
    );
    return 0;
  }
}
